/**
 * Graph-theoretic target distances + feedback-edge marking for the SGD
 * stress phase.
 *
 * Target distances are weighted shortest paths where each edge costs its
 * ideal center-to-center length: the solver's per-edge ideal GAP (silhouette
 * edge-to-edge, the same value the springs use) plus both endpoints' mean
 * radii. Using the same ideal-length source as every other phase keeps the
 * SGD equilibrium and the spring equilibrium the same point — the phases
 * cooperate instead of fighting.
 *
 * Pairs with no path (possible inside a component united only by shared
 * external-port peers) get a substitute distance — 1.5× the longest finite
 * target in the component — at a strongly reduced weight, so the subgraphs
 * separate cleanly without the seed dictating their relative placement.
 */

export interface DistEdge {
  readonly from: string;
  readonly to: string;
  /** Ideal silhouette gap (same units the springs consume). */
  readonly idealLen: number;
}

export interface TargetPair {
  /** Indexes into the sorted id array handed to buildTargetDistances. */
  readonly i: number;
  readonly j: number;
  /** Target center-to-center distance. */
  readonly d: number;
  /** SGD weight (d⁻², scaled down for substitute pairs). */
  readonly w: number;
}

const SUBSTITUTE_DIST_FACTOR = 1.5;
const SUBSTITUTE_W_SCALE = 0.15;

/** Mean radius: average of half-width and half-height. A direction-free
 *  stand-in for the silhouette radius used by the springs. */
export function meanRadius(size: { w: number; h: number }): number {
  return (size.w + size.h) / 4;
}

/**
 * All-pairs weighted shortest paths over the component's edges.
 * `ids` MUST be sorted (deterministic pair order falls out of it).
 * Returns pairs for i < j only.
 */
export function buildTargetDistances(
  ids: readonly string[],
  edges: readonly DistEdge[],
  size: ReadonlyMap<string, { w: number; h: number }>,
): TargetPair[] {
  const n = ids.length;
  const idx = new Map<string, number>();
  ids.forEach((id, i) => idx.set(id, i));

  const adj: { to: number; len: number }[][] = Array.from({ length: n }, () => []);
  for (const e of edges) {
    const a = idx.get(e.from);
    const b = idx.get(e.to);
    if (a === undefined || b === undefined || a === b) continue;
    const len =
      e.idealLen + meanRadius(size.get(e.from)!) + meanRadius(size.get(e.to)!);
    adj[a]!.push({ to: b, len });
    adj[b]!.push({ to: a, len });
  }

  // Dijkstra from each source. Component sizes are board-scale (≤ a few
  // hundred); the O(n²) selection loop beats heap overhead here.
  const dist = new Float64Array(n * n).fill(Infinity);
  for (let s = 0; s < n; s++) {
    const row = s * n;
    dist[row + s] = 0;
    const done = new Uint8Array(n);
    for (;;) {
      let u = -1;
      let best = Infinity;
      for (let i = 0; i < n; i++) {
        if (!done[i] && dist[row + i]! < best) {
          best = dist[row + i]!;
          u = i;
        }
      }
      if (u < 0) break;
      done[u] = 1;
      const du = dist[row + u]!;
      for (const { to, len } of adj[u]!) {
        if (du + len < dist[row + to]!) dist[row + to] = du + len;
      }
    }
  }

  let maxFinite = 0;
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      const d = dist[i * n + j]!;
      if (Number.isFinite(d) && d > maxFinite) maxFinite = d;
    }
  }
  // No finite pair at all (edge-free CC united purely by port peers):
  // base the substitute on a generic two-hop-ish scale from node sizes.
  if (maxFinite === 0) {
    let meanR = 0;
    for (const id of ids) meanR += meanRadius(size.get(id)!);
    meanR /= Math.max(1, n);
    maxFinite = 2 * (2 * meanR + 100);
  }
  const dSub = SUBSTITUTE_DIST_FACTOR * maxFinite;

  const pairs: TargetPair[] = [];
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      const d = dist[i * n + j]!;
      if (Number.isFinite(d) && d > 0) {
        pairs.push({ i, j, d, w: 1 / (d * d) });
      } else {
        pairs.push({ i, j, d: dSub, w: SUBSTITUTE_W_SCALE / (dSub * dSub) });
      }
    }
  }
  return pairs;
}

/**
 * Every directed edge whose endpoints share a strongly-connected component,
 * as `from|to` keys. Flow ordering only means anything on the condensation
 * DAG — inside an SCC every member reaches every other, so no left→right
 * order between them is defensible. Exempting only DFS back edges (the old
 * rule) kept the REST of a cycle's edges as full-rank constraints: the
 * arbitrary DFS visit order stretched every cycle into a line, with the
 * closing edge doubled back across it. All intra-SCC edges are exempt from
 * flow constraints and rank derivation; the stress geometry disposes.
 *
 * Iterative Tarjan over sorted adjacency from sorted roots — deterministic
 * under input permutation (component membership is order-free anyway; the
 * traversal order is fixed for good measure).
 */
export function intraSccEdgeKeys(
  ids: readonly string[],
  edges: readonly { from: string; to: string }[],
): Set<string> {
  const compOf = sccComponents(ids, edges);
  const out = new Set<string>();
  for (const e of edges) {
    if (e.from === e.to) continue;
    const a = compOf.get(e.from);
    if (a !== undefined && a === compOf.get(e.to)) {
      out.add(`${e.from}|${e.to}`);
    }
  }
  return out;
}

/**
 * Strongly-connected component id per node (iterative Tarjan over sorted
 * adjacency from sorted roots — deterministic under input permutation).
 * Consumers: intra-SCC flow exemption above, and the solver seed (SCC
 * members of a cycle seed on a ring, not a collinear stack).
 */
export function sccComponents(
  ids: readonly string[],
  edges: readonly { from: string; to: string }[],
): Map<string, number> {
  const adjOut = new Map<string, string[]>();
  for (const id of ids) adjOut.set(id, []);
  for (const e of edges) {
    if (e.from === e.to) continue;
    adjOut.get(e.from)?.push(e.to);
  }
  for (const list of adjOut.values()) list.sort();

  const index = new Map<string, number>();
  const low = new Map<string, number>();
  const onStack = new Set<string>();
  const stack: string[] = [];
  const compOf = new Map<string, number>();
  let nextIndex = 0;
  let nextComp = 0;

  const sortedIds = [...ids].sort();
  for (const root of sortedIds) {
    if (index.has(root)) continue;
    // Frames: [node, next-child-index].
    const frames: [string, number][] = [[root, 0]];
    index.set(root, nextIndex);
    low.set(root, nextIndex);
    nextIndex++;
    stack.push(root);
    onStack.add(root);
    while (frames.length > 0) {
      const frame = frames[frames.length - 1]!;
      const [node] = frame;
      const children = adjOut.get(node) ?? [];
      if (frame[1] < children.length) {
        const child = children[frame[1]]!;
        frame[1]++;
        if (!index.has(child)) {
          index.set(child, nextIndex);
          low.set(child, nextIndex);
          nextIndex++;
          stack.push(child);
          onStack.add(child);
          frames.push([child, 0]);
        } else if (onStack.has(child)) {
          low.set(node, Math.min(low.get(node)!, index.get(child)!));
        }
        continue;
      }
      frames.pop();
      const parent = frames[frames.length - 1];
      if (parent) {
        low.set(parent[0], Math.min(low.get(parent[0])!, low.get(node)!));
      }
      if (low.get(node) === index.get(node)) {
        const comp = nextComp++;
        for (;;) {
          const member = stack.pop()!;
          onStack.delete(member);
          compOf.set(member, comp);
          if (member === node) break;
        }
      }
    }
  }
  return compOf;
}
